import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useMemo} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
  IconName,
} from '@lightricks/react-design-system';
import DropdownButton from '@contentlab/components/dropdown-button';
import {
  ProjectsStatusFilter,
  ProjectsToolFilter,
} from '@contentlab/stores/projectsStore';
import {FeatureName} from '@contentlab/types/project';
import {isFeatureAvailableToUser} from '@contentlab/utils/featureUtils';
import getFeatureIconName from '@contentlab/utils/getFeatureIconName';
import translate from '@/utils/translate';

interface ProjectsPageHeaderProps {
  testID?: string;
  selectedSortId?: SortId;
  selectedToolFilter?: ProjectsToolFilter;
  selectedStatusFilter?: ProjectsStatusFilter;
  onSortChange?: (sortOption: SortId) => void;
  onToolsFilterChange?: (toolsFilter: ProjectsToolFilter) => void;
  onStatusFilterChange?: (statusFilter: ProjectsStatusFilter) => void;
}

const HeaderContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '24px 40px',
  boxSizing: 'border-box',
  position: 'sticky',
  top: 0,
  background: designSystemToken('semantic.bg.secondary'),
  zIndex: 1,
}));

const FiltersContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  gap: '8px',
  alignItems: 'center',
}));

export enum SortId {
  RECENTLY_UPDATED = 'recently-updated',
  NAME_A_TO_Z = 'a-z',
  NEWEST = 'newest',
  OLDEST = 'oldest',
}

function ProjectsPageHeader({
  testID = 'projects-page-header',
  selectedSortId = SortId.RECENTLY_UPDATED,
  selectedToolFilter = ProjectsToolFilter.ALL,
  selectedStatusFilter = ProjectsStatusFilter.ALL,
  onSortChange,
  onToolsFilterChange,
  onStatusFilterChange,
}: ProjectsPageHeaderProps) {
  const flags = useFlags();

  type Option = {
    id: string;
    title: string;
    icon: IconName;
  };

  const sortMenuOptions: Option[] = useMemo(
    () => [
      {
        id: SortId.RECENTLY_UPDATED,
        title: translate('Recently updated'),
        icon: 'Features-Timer-Deactive',
      },
      {
        id: SortId.NAME_A_TO_Z,
        title: translate('Name (A-Z)'),
        icon: 'Navigation-List',
      },
      {
        id: SortId.NEWEST,
        title: translate('Newest first'),
        icon: 'Navigation-Sort-Ascending',
      },
      {
        id: SortId.OLDEST,
        title: translate('Oldest first'),
        icon: 'Navigation-Sort-Descending',
      },
    ],
    []
  );

  const toolsMenuOptions: Option[] = useMemo(() => {
    const options: Option[] = [
      {
        id: ProjectsToolFilter.ALL,
        title: translate('All tools'),
        icon: 'Navigation-List',
      },
      {
        id: ProjectsToolFilter.RESIZE,
        title: translate('Resize'),
        icon: getFeatureIconName(FeatureName.REFORMATTING),
      },
      {
        id: ProjectsToolFilter.DUB,
        title: translate('Dub'),
        icon: getFeatureIconName(FeatureName.AI_DUBBING),
      },
      {
        id: ProjectsToolFilter.REPHRASE,
        title: translate('Rescript'),
        icon: getFeatureIconName(FeatureName.REPHRASING),
      },
      {
        id: ProjectsToolFilter.CAPTIONS,
        title: translate('Captions'),
        icon: getFeatureIconName(FeatureName.CAPTIONS),
      },
      {
        id: ProjectsToolFilter.UPSCALE,
        title: translate('Upscale'),
        icon: getFeatureIconName(FeatureName.UPSCALE),
      },
      {
        id: ProjectsToolFilter.AI_AVATAR,
        title: translate('Avatars'),
        icon: getFeatureIconName(FeatureName.AI_AVATAR),
      },
    ];
    return options.filter(
      (option) =>
        option.id === ProjectsToolFilter.ALL ||
        isFeatureAvailableToUser(option.id as FeatureName, flags)
    );
  }, [flags]);

  const statusMenuOptions: Option[] = useMemo(
    () => [
      {
        id: ProjectsStatusFilter.ALL,
        title: translate('All statuses'),
        icon: 'Navigation-List',
      },
      {
        id: ProjectsStatusFilter.READY_TO_USE,
        title: translate('Ready to use'),
        icon: 'Actions-Accept',
      },
      {
        id: ProjectsStatusFilter.IN_PROGRESS,
        title: translate('In progress'),
        icon: 'Features-Timer-Deactive',
      },
    ],
    []
  );

  const selectedSortOption = sortMenuOptions.find(
    (option) => option.id === selectedSortId
  );
  const selectedToolOption = toolsMenuOptions.find(
    (option) => option.id === selectedToolFilter
  );
  const selectedStatusOption = statusMenuOptions.find(
    (option) => option.id === selectedStatusFilter
  );

  return (
    <HeaderContainer data-testid={testID}>
      <Headline size="lg">{translate('My Projects')}</Headline>
      <FiltersContainer>
        <DropdownButton
          testID="sort-dropdown-button"
          label={selectedSortOption?.title || translate('Recently updated')}
          menuOptions={sortMenuOptions.map((option) => ({
            ...option,
            onClick: () => {
              onSortChange?.(option.id as SortId);
            },
          }))}
          selectedOptionId={selectedSortOption?.id}
          startIcon={
            <Icon
              size="small"
              appearance="neutral"
              name="Navigation-Arrow-Down-Up"
            />
          }
          iconSize="small"
          onClearButtonClick={
            selectedSortOption?.id !== SortId.RECENTLY_UPDATED
              ? () => {
                  onSortChange?.(SortId.RECENTLY_UPDATED);
                }
              : undefined
          }
        />
        <DropdownButton
          testID="tools-filter-button"
          label={selectedToolOption?.title || translate('All tools')}
          menuOptions={toolsMenuOptions.map((option) => ({
            ...option,
            onClick: () => {
              onToolsFilterChange?.(option.id as ProjectsToolFilter);
            },
          }))}
          selectedOptionId={selectedToolOption?.id}
          iconSize="small"
          onClearButtonClick={
            selectedToolOption?.id !== ProjectsToolFilter.ALL
              ? () => {
                  onToolsFilterChange?.(ProjectsToolFilter.ALL);
                }
              : undefined
          }
        />
        <DropdownButton
          testID="status-filter-button"
          label={selectedStatusOption?.title || translate('All statuses')}
          menuOptions={statusMenuOptions.map((option) => ({
            ...option,
            onClick: () => {
              onStatusFilterChange?.(option.id as ProjectsStatusFilter);
            },
          }))}
          selectedOptionId={selectedStatusOption?.id}
          iconSize="small"
          onClearButtonClick={
            selectedStatusOption?.id !== ProjectsStatusFilter.ALL
              ? () => {
                  onStatusFilterChange?.(ProjectsStatusFilter.ALL);
                }
              : undefined
          }
        />
      </FiltersContainer>
    </HeaderContainer>
  );
}

export default ProjectsPageHeader;
